import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  Save,
  X,
  Folder,
  Image,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import API_BASE_URL from "../../../services/api";
import Button from "../../../components/ui/Button";
import { canAccess } from "../../../utils/permissions";
import { useAuth } from "../../../context/AuthContext";
import axiosInstance from "../../../utils/axiosInstance";

const SubcategoryItem = () => {
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const { user: currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [formData, setFormData] = useState({
    name: "",
    catId: "",
    image: null,
  });
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [categoriesRes, subcategoriesRes] = await Promise.all([
        axiosInstance.get(`${API_BASE_URL}/api/categories`),
        axiosInstance.get(`${API_BASE_URL}/api/subcategories`),
      ]);

  
      setCategories(categoriesRes.data);
      setSubcategories(subcategoriesRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    if (!formData.name.trim()) {
      alert("Please enter a subcategory name");
      return;
    }

    if (!formData.catId) {
      alert("Please select a category");
      return;
    }

    setSubmitting(true);

    try {
      const submitData = new FormData();
      submitData.append("name", formData.name.trim());
      submitData.append("catId", formData.catId);

      if (formData.image) {
        submitData.append("image", formData.image);
      }

      if (editingSubcategory) {
        await axios.put(
          `${API_BASE_URL}/api/subcategories/${editingSubcategory.id}`,
          submitData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          },
        );
      } else {
        await axios.post(`${API_BASE_URL}/api/subcategories`, submitData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      resetForm();
      fetchData();
    } catch (error) {
      console.error("Error saving subcategory:", error);
      alert(
        `Failed to ${editingSubcategory ? "update" : "create"} subcategory`,
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        alert("Please select a valid image file");
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert("Image size should be less than 5MB");
        return;
      }

      setFormData((prev) => ({
        ...prev,
        image: file,
      }));

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEdit = (subcategory) => {
    setEditingSubcategory(subcategory);
    setFormData({
      name: subcategory.name,
      catId: subcategory.catId,
      image: null,
    });
    setImagePreview(subcategory.imgUrl || null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this subcategory?"))
      return;

    try {
      await axios.delete(`${API_BASE_URL}/api/subcategories/${id}`);
      fetchData();
    } catch (error) {
      console.error("Error deleting subcategory:", error);

      const msg =
        error?.response?.data?.message || "Failed to delete subcategory";
      const usage = error?.response?.data?.usage;

      if (usage?.length) {
        alert(
          `${msg}\n\nUsed in:\n- ${usage.map((u) => `${u.label}: ${u.count}`).join("\n- ")}`,
        );
      } else {
        alert(msg);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      catId: "",
      image: null,
    });
    setImagePreview(null);
    setEditingSubcategory(null);
  };

  const removeImage = () => {
    setFormData((prev) => ({ ...prev, image: null }));
    setImagePreview(null);
    // Reset file input
    const fileInput = document.getElementById("subcategory-image-upload");
    if (fileInput) fileInput.value = "";
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const getSubcategoriesByCategory = (categoryId) => {
    return subcategories.filter((sub) => sub.catId === categoryId);
  };

  const filteredCategories = categories.filter(
    (category) =>
      category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getSubcategoriesByCategory(category.id).some((sub) =>
        sub.name.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Side - Add/Edit Form */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg border border-gray-200 p-4 sticky top-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">
                {editingSubcategory
                  ? "Edit Subcategory"
                  : "Add New Subcategory"}
              </h3>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Category *
                  </label>
                  <select
                    required
                    value={formData.catId}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        catId: e.target.value,
                      }))
                    }
                    disabled={submitting}
                    className="w-full px-3 py-2 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
                    <option value="">Select Category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Subcategory Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, name: e.target.value }))
                    }
                    className="w-full px-3 py-2 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter subcategory name"
                    disabled={submitting}
                  />
                </div>
 <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Category Type
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => {
                    setFormData((prev) => ({ ...prev, type: e.target.value }));
                  
                  }}
                  className={`w-full px-3 py-2 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 `}
                  disabled={submitting}>
                  <option value="">Select Type</option>
                
                  <option value="VITAL_ORGAN">Vital Organ</option>
                  <option value="PROFILE">Health Profile</option>
                  <option value="CURATED_CHECKUP">Curated Check Ups</option>
                </select>
                
              </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Subcategory Image
                  </label>
                  <div className="border border-dashed border-gray-300 rounded-md p-3 text-center">
                    {imagePreview ||
                    (editingSubcategory && editingSubcategory.imgUrl) ? (
                      <div className="space-y-2">
                        <img
                          src={imagePreview || editingSubcategory.imgUrl}
                          alt="Preview"
                          className="mx-auto h-16 w-16 object-cover rounded-md"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={removeImage}
                          disabled={submitting}
                          className="text-red-600 hover:text-red-800 text-xs">
                          Remove
                        </Button>
                      </div>
                    ) : (
                      <div className="py-2">
                        <Image className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                        <p className="text-xs text-gray-600 mb-2">
                          Click to upload image
                        </p>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="hidden"
                          id="subcategory-image-upload"
                          disabled={submitting}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          iconName="Plus"
                          onClick={() =>
                            document
                              .getElementById("subcategory-image-upload")
                              .click()
                          }
                          disabled={submitting}
                          className="text-xs">
                          Choose Image
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex space-x-2 pt-2">
                  <Button
                    type="submit"
                    variant="solid"
                    size="sm"
                    iconName={submitting ? "Loader" : "Save"}
                    disabled={
                      !canAccess(currentUser, "canCreate") ||
                      submitting ||
                      !formData.name.trim() ||
                      !formData.catId
                    }
                    loading={submitting}
                    className="bg-purple-600 hover:bg-purple-700 text-white text-xs px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200">
                    {editingSubcategory ? "Update" : "Create"} Subcategory
                  </Button>

                  {editingSubcategory && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      iconName="X"
                      onClick={resetForm}
                      disabled={submitting}
                      className="text-xs disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200">
                      Cancel
                    </Button>
                  )}
                </div>
              </form>
            </div>
          </div>

          {/* Right Side - Subcategories List */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              {/* Search Header */}
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h1 className="text-sm font-semibold text-gray-900">
                      Subcategories List
                    </h1>
                    <p className="text-xs text-gray-600 mt-0.5">
                      {subcategories.length} subcategories found
                    </p>
                  </div>
                </div>
              </div>

              {/* Search and Filters */}
              <div className="p-4 border-b border-gray-200">
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Search Subcategories
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                        <Search className="w-4 h-4 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        placeholder="Search by subcategory name..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="block w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md bg-white placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-xs"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Categories Tree */}
              <div className="p-4">
                {loading ? (
                  <div className="text-center py-8">
                    <div className="flex justify-center items-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600"></div>
                    </div>
                    <p className="mt-2 text-xs text-gray-500">
                      Loading subcategories...
                    </p>
                  </div>
                ) : filteredCategories.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="flex flex-col items-center justify-center">
                      <Folder className="w-12 h-12 text-gray-400 mb-2" />
                      <p className="text-gray-500 font-medium text-xs">
                        No categories found
                      </p>
                      <p className="text-gray-400 text-xs mt-0.5">
                        {searchTerm
                          ? "Try adjusting your search"
                          : "No categories have been added yet"}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredCategories.map((category) => {
                      const categorySubcategories = getSubcategoriesByCategory(
                        category.id,
                      );
                      const isExpanded = expandedCategories.has(category.id);

                      return (
                        <div
                          key={category.id}
                          className="border border-gray-200 rounded-lg overflow-hidden">
                          {/* Category Header */}
                          <div
                            className="flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors duration-200"
                            onClick={() => toggleCategory(category.id)}>
                            <div className="flex items-center space-x-3">
                              {isExpanded ? (
                                <ChevronDown className="w-4 h-4 text-gray-500" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-gray-500" />
                              )}
                              {category.imgUrl ? (
                                <img
                                  src={category.imgUrl}
                                  alt={category.name}
                                  className="w-8 h-8 rounded-md object-cover"
                                />
                              ) : (
                                <Folder className="w-8 h-8 text-gray-400" />
                              )}
                              <div>
                                <h3 className="text-sm font-medium text-gray-900">
                                  {category.name}
                                </h3>
                                <p className="text-xs text-gray-600">
                                  {categorySubcategories.length} subcategories
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Subcategories */}
                          {isExpanded && (
                            <div className="bg-white border-t border-gray-200">
                              {categorySubcategories.length === 0 ? (
                                <div className="p-4 text-center">
                                  <p className="text-xs text-gray-600">
                                    No subcategories found
                                  </p>
                                </div>
                              ) : (
                                <div className="p-4 space-y-3">
                                  {categorySubcategories.map(
                                    (subcategory, index) => (
                                      <div
                                        key={subcategory.id}
                                        className={`flex items-center justify-between p-3 border rounded-lg transition-all duration-200 ${
                                          editingSubcategory?.id ===
                                          subcategory.id
                                            ? "border-purple-300 bg-purple-50"
                                            : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                                        }`}>
                                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                                          {/* Serial Number */}
                                          <div className="text-gray-500 font-medium text-xs w-6">
                                            {index + 1}
                                          </div>

                                          {/* Subcategory Image */}
                                          <div className="flex-shrink-0">
                                            {subcategory.imgUrl ? (
                                              <img
                                                src={subcategory.imgUrl}
                                                alt={subcategory.name}
                                                className="w-10 h-10 rounded-md object-cover"
                                              />
                                            ) : (
                                              <div className="w-10 h-10 bg-gray-100 rounded-md flex items-center justify-center">
                                                <Image className="w-5 h-5 text-gray-400" />
                                              </div>
                                            )}
                                          </div>

                                          {/* Subcategory Info */}
                                          <div className="flex-1 min-w-0">
                                            <h3 className="text-sm font-medium text-gray-900 truncate">
                                              {subcategory.name}
                                            </h3>
                                            <div className="text-xs text-gray-500 mt-1">
                                              Created:{" "}
                                              {subcategory.createdAt
                                                ? new Date(
                                                    subcategory.createdAt,
                                                  ).toLocaleDateString()
                                                : "N/A"}
                                            </div>
                                          </div>
                                        </div>

                                        {/* Actions */}
                                        <div className="flex space-x-1">
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            iconName="Edit2"
                                            disabled={
                                              !canAccess(
                                                currentUser,
                                                "canEdit",
                                              ) || submitting
                                            }
                                            className={`
                                            text-blue-600 border border-blue-200 text-xs px-2 py-1
                                            ${
                                              !canAccess(
                                                currentUser,
                                                "canEdit",
                                              ) || submitting
                                                ? "opacity-40 cursor-not-allowed hover:text-blue-600 hover:border-blue-200"
                                                : "hover:text-blue-800 hover:border-blue-300"
                                            }
                                            transition-all duration-200
                                          `}
                                            onClick={() =>
                                              canAccess(
                                                currentUser,
                                                "canEdit",
                                              ) && handleEdit(subcategory)
                                            }
                                          />

                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            iconName="Trash2"
                                            disabled={
                                              !canAccess(
                                                currentUser,
                                                "canDelete",
                                              ) || submitting
                                            }
                                            className={`
                                            text-red-600 border border-red-200 text-xs px-2 py-1
                                            ${
                                              !canAccess(
                                                currentUser,
                                                "canDelete",
                                              ) || submitting
                                                ? "opacity-40 cursor-not-allowed hover:text-red-600 hover:border-red-200"
                                                : "hover:text-red-800 hover:border-red-300"
                                            }
                                            transition-all duration-200
                                          `}
                                            onClick={() =>
                                              canAccess(
                                                currentUser,
                                                "canDelete",
                                              ) && handleDelete(subcategory.id)
                                            }
                                          />
                                        </div>
                                      </div>
                                    ),
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubcategoryItem;
